// ServiceWebPage.jsx
import React from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Tabs,
  Tab,
  Container,
} from "@mui/material";
import { useTheme, createTheme, ThemeProvider } from "@mui/material/styles";
import { Fade, Slide } from "react-awesome-reveal";

// ICONOS
import CodeIcon from "@mui/icons-material/Code";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import SpeedIcon from "@mui/icons-material/Speed";
import EditIcon from "@mui/icons-material/Edit";
import ImageIcon from "@mui/icons-material/Image";
import MovieCreationIcon from "@mui/icons-material/MovieCreation";
import CampaignIcon from "@mui/icons-material/Campaign";
import BarChartIcon from "@mui/icons-material/BarChart";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Tipografías (npm i @fontsource/red-hat-display @fontsource/merriweather-sans)
import "@fontsource/red-hat-display/700.css";
import "@fontsource/red-hat-display/900.css";
import "@fontsource/merriweather-sans/400.css";
import "@fontsource/merriweather-sans/500.css";

// Bullets minimalistas (dot + borde suave)
// Bullets minimalistas (dot + halo suave) — compacto
const BULLET_STYLE = {
  listStyle: "none",
  p: 0,
  ml: 3,
  "& li": {
    position: "relative",
    paddingLeft: 4, // antes 18
    lineHeight: 1.35, // antes 1.45
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "0.6em", // alinea con altura de texto
      width: 6,
      height: 6,
      borderRadius: "50%",
      backgroundColor: "rgba(25,36,47,.78)",
      boxShadow: "0 0 0 1.5px rgba(25,36,47,.08)",
      transform: "translateY(-50%)",
    },
  },
  // separador controlado (en vez de marginBottom por item)
  "& li + li": { marginTop: 3 },
};

const BRAND = {
  primary: "#F35A3A",
  dark: "#19242F",
  lightBG: "#fff",
};

const WHATSAPP_PHONE = "5491112345678"; // <-- reemplazá por el tuyo
const WA_BASE = "https://wa.me/";
const buildWaLink = (label) =>
  `${WA_BASE}${WHATSAPP_PHONE}?text=${encodeURIComponent(
    `Hola 👋\nQuiero más info sobre: ${label}.`
  )}`;

/* ===================== DATA ===================== */
const TABS = [
  {
    key: "desarrollo",
    label: "Desarrollo",
    items: [
      {
        icon: <CodeIcon />,
        title: "LANDING PAGE DE CONVERSIÓN",
        bullets: [
          "Página sencilla para crear contacto.",
          "Presentación y testimonios.",
          "Ajustada a tu negocio.",
          "Integración de formularios.",
          "IDEAL PARA MARKETING.",
        ],
        cta: "Quiero mi landing",
      },
      {
        icon: <DesignServicesIcon />,
        title: "SITIO WEB PERSONALIZADO",
        bullets: [
          "Estructura completa.",
          "Secciones personalizadas.",
          "Escalable según tus necesidades.",
          "Posicinamiento en motores de búsqueda.",
          "Responsividad premium para celulares y PC.",
          "IDEAL PARA NEGOCIOS ESTABLECIDOS.",
        ],
        cta: "Quiero mi web",
      },
      {
        icon: <SpeedIcon />,
        title: "SOLUCIONES WEB A MEDIDA",
        bullets: [
          "Análisis de lógica de negocios.",
          "INTEGRACIONES: pasarela de pagos, CRM, WhatsApp, APIs.",
          "Panel, lógicas y automatizaciones.",
          "Seguridad y roles (cookies, permisos, vistas).",
          "Controladores grandes.",
          "Capacitación de tu personal.",
          "GESTIÓN DIGITAL AVANZADA PARA TU NEGOCIO",
        ],
        cta: "Agendar consulta",
      },
    ],
  },
  {
    key: "contenido",
    label: "Contenido",
    items: [
      {
        icon: <EditIcon />,
        title: "OPTIMIZACIÓN DE REDES SOCIALES",
        bullets: [
          "Mejoramos tu biografía e historias destacadas.",
          "Guía de tono y estilo para consistencia de marca.",
          "Calendario editorial base con copywriting.",
          "Contenido entregado: anuncios, carruseles, reels.",
          "Llamados a la acción probados para conversión.",
          "Estadísticas base y estrategias de publicación.",
        ],
        cta: "¡Empecemos ya!",
      },
      {
        icon: <ImageIcon />,
        title: "SESIONES FOTOGRÁFICAS PROFESIONALES",
        bullets: [
          "Dirección personalizada.",
          "Retratos, productos, y contextos de uso.",
          "Pre/producción: tomas y locaciones.",
          "Edición y exportes multi–plataforma.",
          "Entregas en tiempo récord.",
          "Instrucciones de uso.",
        ],
        cta: "Agendar ahora",
      },
      {
        icon: <MovieCreationIcon />,
        title: "VIDEOS: REELS & LONGFORMAT",
        bullets: [
          "Guiones exitosos probados.",
          "Reels, UCG, testimonios, presentaciones.",
          "Subtítulos y transiciones exitosas.",
          "Estadísitcas: retención, clicks, comentarios.",
          "Publicación estratégica.",
          "Seguimiento de resultados.",
        ],
        cta: "Crear videos",
      },
    ],
  },
  {
    key: "campanias",
    label: "Campañas",
    items: [
      {
        icon: <CampaignIcon />,
        title: "ADS EN META / GOOGLE",
        bullets: [
          "Estructura de campañas.",
          "Creatividad y profesionalismo.",
          "Ideal para eventos o novedades.",
          "Pruebas y mejoras continuas.",
          "Reporte con estadísticas.",
        ],
        cta: "Larguemos un anuncios",
      },
      {
        icon: <BarChartIcon />,
        title: "SEGMENTACIONES Y ANÁLISIS",
        bullets: [
          "Audiencias e intereses.",
          "Recorrido de usuarios.",
          "Estadísticas de conversión.",
          "Informes detallados.",
          "Plan de mejoras basado en datos.",
        ],
        cta: "Quiero segmentar mejor",
      },
      {
        icon: <SyncAltIcon />,
        title: "MARKETING INTEGRAL",
        bullets: [
          "Plan 360°: web + contenido + anuncios.",
          "Mensajería consistente.",
          "Email Marketing para nutrir y cerrar ventas.",
          "Calendario trimestral.",
          "Guías, capacitación y reportes.",
          "Avances y mejoras quincenales.",
        ],
        cta: "Quiero un plan completo",
      },
    ],
  },
];

/* ===================== THEME LOCAL ===================== */
const localTheme = createTheme({
  typography: {
    fontFamily:
      '"Merriweather Sans", system-ui, -apple-system, "Segoe UI", Roboto, Arial',
    h1: { fontFamily: '"Montserrat", sans-serif' },
    h2: { fontFamily: '"Montserrat", sans-serif' },
    h3: { fontFamily: '"Montserrat", sans-serif' },
    h4: { fontFamily: '"Montserrat", sans-serif' },
    h5: { fontFamily: '"Montserrat", sans-serif' },
    h6: { fontFamily: '"Montserrat", sans-serif' },
  },
  palette: {
    primary: { main: BRAND.primary },
    text: { primary: BRAND.dark },
    background: { default: BRAND.lightBG },
  },
});

/* ===================== UI PRIMITIVES ===================== */
function SectionHeader({ title, subtitle }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <Box sx={{ textAlign: "center", mb: 3 }}>
      <Typography
        variant={isMobile ? "h4" : "h3"}
        sx={{ fontWeight: 900, color: BRAND.dark, mb: 1 }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="body1"
          sx={{
            maxWidth: 920,
            mx: "auto",
            color: "text.secondary",
            whiteSpace: "pre-line",
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

function ServiceCard({ icon, title, bullets = [], cta, delay = 0, pro = false }) {
  return (
    <Slide direction="up" triggerOnce delay={delay} style={{ width: "100%" }}>
      <Card
        elevation={0}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 360,
          borderRadius: 3,
          bgcolor: "#fff",
          border: "1px solid rgba(25,36,47,0.06)",
          boxShadow: "0 10px 24px rgba(0,0,0,0.06)",
          overflow: "hidden",
          transition: "transform .18s, box-shadow .18s, border-color .18s",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 16px 40px rgba(0,0,0,0.12)",
            borderColor: "rgba(25,36,47,0.12)",
          },
        }}
      >
        {/* Banner título */}
        <Box
          sx={{
            position: "relative",
            bgclip: "padding-box",
            background:
              "linear-gradient(180deg, rgba(243,90,58,1) 0%, rgba(233,83,52,1) 100%)",
            color: "#fff",
            px: 2.2,
            py: 1.6,
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            borderBottom: "1px solid rgba(255,255,255,.15)",
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "999px",
              bgcolor: "#fff",
              color: BRAND.primary,
              display: "grid",
              placeItems: "center",
              boxShadow: "0 2px 10px rgba(0,0,0,.12)",
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>

          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 900,
              lineHeight: 1.5,
              letterSpacing: 1.5,
              textShadow: "0 1px 0 rgba(0,0,0,.18)",
            }}
          >
            {title}
          </Typography>

          {/* Chip PRO alineado a la derecha */}
    {pro && (
      <Box sx={{ ml: "auto" }}>
        <Box
          component="span"
          sx={{
            display: "inline-block",
            fontSize: 16,
            fontWeight: 700,
            lineHeight: "20px",
            px: 1.2,
            py: 0.5,
            borderRadius: 999,
            letterSpacing: 0.6,
            backgroundColor: "#19242F",
            color: "#fff",
            boxShadow: "inset 0 -1px 0 rgba(255,255,255,.18)",
          }}
        >
          Pro
        </Box>
      </Box>
    )}
        </Box>

        {/* Contenido */}
        <CardContent
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
            flexGrow: 1,
          }}
        >
          <Stack component="ul" spacing={0} sx={BULLET_STYLE}>
            {bullets.map((b, i) => (
              <Box key={i} component="li">
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {b}
                </Typography>
              </Box>
            ))}
          </Stack>

          <Box sx={{ flexGrow: 1 }} />

          <Stack direction="row" alignItems="center" justifyContent="flex-end">
<Button
  component="a"
  href={buildWaLink(title)}
  target="_blank"
  rel="noopener noreferrer"
  variant="contained"
  sx={{
    textTransform: "none",
    fontWeight: 900,
    borderRadius: 2,
    bgcolor: BRAND.dark,          // ← dark
    color: "#fff",
    px: 2.2,
    ":hover": { bgcolor: "#0f1821" },
  }}
>
  {cta}
</Button>

          </Stack>
        </CardContent>
      </Card>
    </Slide>
  );
}

/* ===================== PAGE ===================== */
export default function ServiceWebPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = React.useState(0);

  return (
    <ThemeProvider theme={localTheme}>
      <Box sx={{ background: BRAND.lightBG, minHeight: "100vh" }}>
        {/* HERO SIMPLE */}
        <Box
          sx={{
            px: { xs: 2, md: 6 },
            pt: { xs: 6, md: 10 },
            pb: { xs: 3, md: 2 },
            textAlign: "center",
            background:
              "radial-gradient(80rem 40rem at 50% -10%, rgba(243,90,58,.12), #ffffff 50%)",
          }}
        >
          <Fade triggerOnce>
            <SectionHeader
              title="Presencia digital integral"
              subtitle={`Te acompañamos en todo el proceso: desarrollo del sitio, creación de contenido y campañas que convierten.\nTe asesoramos para adaptar y optimizar tu lógica de negocios.`}
            />
          </Fade>
        </Box>

        {/* TABS */}
        <Container maxWidth="xl" disableGutters sx={{ px: { xs: 2, md: 4 } }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant={isMobile ? "scrollable" : "fullWidth"}
            scrollButtons={isMobile ? "auto" : false}
            sx={{
              borderRadius: 2,
              mb: 3,
              bgcolor: "#fff",
              border: "1px solid rgba(243,90,58,0.18)",
              boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
              "& .MuiTab-root": { textTransform: "none", fontWeight: 800 },
              "& .Mui-selected": { color: BRAND.primary },
              "& .MuiTabs-indicator": {
                backgroundColor: BRAND.primary,
                height: 3,
                borderRadius: 3,
              },
            }}
          >
            {TABS.map((t) => (
              <Tab key={t.key} label={t.label} />
            ))}
          </Tabs>

          {/* PANELS */}
          {TABS.map((t, idx) => (
            <Box key={t.key} role="tabpanel" hidden={tab !== idx}>
              {tab === idx && (
                <Box sx={{ pb: { xs: 6, md: 10 } }}>
                  <Grid
                    container
                    spacing={{ xs: 2, md: 3 }}
                    alignItems="stretch"
                  >
{t.items.map((item, i) => (
  <Grid item xs={12} md={4} key={item.title} sx={{ display: "flex", flexBasis:{ md:"calc((100% - 48px) / 3)" }, maxWidth:{ md:"calc((100% - 48px) / 3)" } }}>
    <ServiceCard {...item} delay={i * 80} pro={i === 2} />
  </Grid>
))}

                  </Grid>
                </Box>
              )}
            </Box>
          ))}
        </Container>
      </Box>
    </ThemeProvider>
  );
}
